import { Avatar, Box, Divider, Grid, Rating, Stack, Typography } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcButton, MprcContainer, MprcJoinComponents, MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator } from 'components';
import { EnrolledUsersOption, ITrainingViewDto, TrainingRatingOption, TrainingViewDto } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import moment from 'moment';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useModalUserProfile } from 'providers/UserProfileProvider';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { publishDiscussionMentionEvent } from 'views/lms/components/discussion/LmsDiscussionEvent';
import { TrainingEditRating } from 'views/lms/student/TrainingRatingView/TrainingEditRating';
import { TrainingRatingViewDialog } from 'views/lms/student/TrainingRatingView/TrainingRatingViewDialog';
import { TrainingListUsersDrawer } from 'views/lms/training/components/TrainingListUsersDrawer';
import { useEnrollmentViewContext } from '..';
import { EnrollmentViewAboutTabs } from './EnrollmentViewAbout';
import { EnrollmentViewCalendar } from './EnrollmentViewCalendar';

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    onChangeTab?: (tab: EnrollmentViewAboutTabs) => void;
}

export const EnrollmentViewOverview: React.FC<IProps> = ({ onChangeTab }) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { showUser } = useModalUserProfile();

    const { enrollment: { trainingId, isPreview, enrollmentId, trainingName, finalizationDate, enableViewEnrolledUsers, discussionEnable, classroomId } } = useEnrollmentViewContext();

    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const [training, setTraining] = useState<TrainingViewDto | null>(null);
    const [trainingView, setTrainingView] = useState<ITrainingViewDto | null>(null);
    const [dialog, setDialog] = useState<any>(null);

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const handleViewEnrollments = (option: EnrolledUsersOption) => {
        setDialog(
            <TrainingListUsersDrawer
                option={option}
                trainingId={training!.trainingId}
                classroomId={classroomId}
                title={
                    option == EnrolledUsersOption.Active
                        ? t("Student.EnrollmentView.ViewActiveEnrollsTitle")
                        : t("Student.EnrollmentView.ViewAllEnrollsTitle")
                }
                subtitle={training?.name}
                onClose={() => setDialog(null)}
            />
        );
    };


    ///
    const doInstructorQuestion = (instructorId: string, name: string) => {
        onChangeTab && onChangeTab("discussion");
        publishDiscussionMentionEvent({ userId: instructorId, name });
    };

    useEffect(() => {
        if (loading) {
            if (classroomId) {
                Promise.all([
                    api.v1.lmsTraining.getTrainingView({ trainingId, incrementTrainingViewCounter: false }),
                    api.v1.lmsTraining.getClassroomsView({ trainingId, classroomId: classroomId, availableOnly: false, ignoreChecks: true, masterEnrollmentId: null })
                ])
                    .then((results) => {
                        setTraining(results[0].data);
                        setTrainingView(results[1].data.length > 0 ? results[1].data[0] : null);
                    })
                    .catch((e: any) => {
                        setTraining(null);
                        setViewError(e);
                    })
                    .finally(() => setLoading(false));
            } else {
                api.v1.lmsTraining.getTrainingView({ trainingId, incrementTrainingViewCounter: false })
                    .then((result) => {
                        setTraining(result.data);
                        setTrainingView(null);
                    })
                    .catch((e) => {
                        setTraining(null);
                        setViewError(e);
                    })
                    .finally(() => setLoading(false));
            }
        }
    }, [loading]);

    return <>

        {dialog}
        <MprcRequestErrorReport error={viewError} />
        {loading && <MprcSpinnerIndicator />}

        {
            training != null && trainingView != null &&
            <>
                {training.ratingOption != TrainingRatingOption.Denied &&
                    <MprcContainer>
                        <Stack justifyContent="space-between" direction="row">
                            <Stack alignItems="center" spacing={1} direction="row">
                                <Rating name="read-only" value={training.ratingAvg} readOnly precision={0.5} />
                                {
                                    training.ratingCount > 0 &&
                                    <MprcLink
                                        color="primary.light"
                                        onClick={() =>
                                            setDialog(
                                                <TrainingRatingViewDialog
                                                    trainingId={trainingId}
                                                    subtitle={trainingName}
                                                    classroomId={classroomId}
                                                    onClose={(success) => {
                                                        setDialog(null);
                                                        if (success) setLoading(true);
                                                    }}
                                                />
                                            )
                                        }
                                    >
                                        <Typography variant="caption" color="primary.light">
                                            <span>(</span>
                                            {t("Training.Literals.StarRatingLong", { count: training.ratingCount })}
                                            <span>)</span>
                                        </Typography>
                                    </MprcLink>
                                }
                            </Stack>

                            {
                                (
                                    trainingView.ratingOption == TrainingRatingOption.AnyTime
                                    ||
                                    (trainingView.ratingOption == TrainingRatingOption.AfterFinalization && finalizationDate != null)
                                ) &&
                                <MprcButton
                                    text={t("Student.EnrollmentView.GiveYourOpinionButtonText")}
                                    disabled={isPreview}
                                    onClick={() => setDialog(
                                        <TrainingEditRating
                                            enrollmentId={enrollmentId}
                                            trainingName={trainingName}
                                            onClose={(success) => {
                                                setDialog(null);
                                                if (success) setLoading(true);
                                            }}
                                        />
                                    )}
                                />
                            }
                        </Stack>
                    </MprcContainer>
                }
                {(training.trainingDurationFormated || training.publishedOn || training.author) &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.TrainingDetailsText")}
                        </Typography>
                        <Typography variant="caption">
                            <MprcJoinComponents
                                separator="&bull;"
                                components={[
                                    training.trainingDurationFormated ? <>{t("Training.Literals.DurationText")}:&nbsp; {training.trainingDurationFormated}</> : null,
                                    training.publishedOn
                                        ? <>
                                            {t("Training.Literals.PublishedOnText")} {Formaters.performaDate(training.publishedOn, { preposition: "auto", excludeTime: true, replaceClosestDays: true })?.toLowerCase()}
                                            {training.modifiedOn && training.modifiedOn > training.publishedOn && ` (${t("Training.Literals.ModifiedOnText")} ${Formaters.performaDate(training.modifiedOn, { excludeTime: true, preposition: "auto", replaceClosestDays: true })}) `.toLocaleLowerCase()}
                                        </>
                                        : null,
                                    training.author ? <>{t("Training.Literals.AuthorText")}: {Formaters.truncateString(training.author, 80)}</> : null
                                ]}
                            />
                        </Typography>
                    </MprcContainer>
                }
                {classroomId != null &&
                    trainingView.instructors != null && trainingView.instructors.length > 0 &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.InstructorText")}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={2}>
                                {trainingView.instructors.map((item, index) =>
                                    <Grid key={index} size={{ xs: 4 }}>
                                        <Stack spacing={1} direction="row" color="muted.main" alignItems="center">
                                            <MprcLink onClick={() => showUser(item.userId)}>
                                                <Avatar src={item.thumbnailUrl} />
                                            </MprcLink>
                                            <Box width="80%">
                                                <Typography variant="subtitle1" fontWeight="bold" component="p" noWrap>{item.name}</Typography>
                                                {discussionEnable &&
                                                    <MprcLink
                                                        color={isPreview ? "muted.light" : "primary.light"}
                                                        onClick={() => doInstructorQuestion(item.userId, item.name)}
                                                        disabled={isPreview}
                                                    >
                                                        {t("Student.EnrollmentView.DoInstructorQuestionButtonText")}
                                                    </MprcLink>
                                                }
                                            </Box>
                                        </Stack>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </MprcContainer>
                }
                {
                    ((trainingView.awardApprovalPoints != null && trainingView.awardApprovalPoints > 0) ||
                        (trainingView.awardEnrollPoints != null && trainingView.awardEnrollPoints > 0)) &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.AwardsText")}
                        </Typography>
                        <Typography variant="subtitle1" color="black">
                            <ul className={AppGlobalClassKeys.unorderedListFlat}>
                                {trainingView.awardEnrollPoints != null && trainingView.awardEnrollPoints > 0 &&
                                    <li>
                                        <Trans
                                            i18nKey={trainingView.awardEnrollPoints == 1 ? "Training.Literals.AwardEnrollPointsLong_one" : "Training.Literals.AwardEnrollPointsLong_other"}
                                            values={{ points: trainingView.awardEnrollPoints }}
                                            t={t}
                                        />
                                    </li>
                                }
                                {trainingView.awardApprovalPoints != null && trainingView.awardApprovalPoints > 0 &&
                                    <li>
                                        <Trans
                                            i18nKey={trainingView.awardApprovalPoints == 1 ? "Training.Literals.AwardApprovalPointsLong_one" : "Training.Literals.AwardApprovalPointsLong_other"}
                                            values={{ points: trainingView.awardApprovalPoints }}
                                            t={t}
                                        />
                                    </li>
                                }
                            </ul>
                        </Typography>
                    </MprcContainer>
                }
                {
                    training.description &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.DescriptionLabel")}
                        </Typography>
                        <Typography variant="subtitle1" color="black">
                            {training.description}
                        </Typography>
                    </MprcContainer>
                }
                {
                    trainingView.certificateFileId != null &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.CertificateText")}
                        </Typography>
                        <Typography variant="subtitle1" color="black">
                            <ul className={AppGlobalClassKeys.unorderedListFlat}>
                                <li>{t("Training.Literals.CertificateHint")}</li>
                                {trainingView.certificateEnableTrainingValidity &&
                                    <li>
                                        {(trainingView.certificateTrainingValidityMonthDay == null)
                                            ? <Trans i18nKey={"Training.Literals.CertificateValidForDays"}
                                                values={{ count: training.certificateTrainingValidityDays ?? 0 }}
                                                t={t}
                                            />
                                            :
                                            <Trans i18nKey={"Training.Literals.CertificateValidAt"}
                                                values={{ date: Formaters.dayMonth(moment(Formaters.padZero(trainingView.certificateTrainingValidityMonthDay, 4), "MMDD").toDate()) }}
                                                t={t}
                                            />
                                        }
                                    </li>
                                }
                            </ul>
                        </Typography>
                    </MprcContainer>
                }
                {
                    training.objectives &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.ObjectivesLabel")}
                        </Typography>
                        <Typography variant="subtitle1" color="black" component="div">
                            <ul className={AppGlobalClassKeys.unorderedListFlat}>
                                {training.objectives?.split("\n").map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                        </Typography>
                    </MprcContainer>
                }
                {(trainingView.usersEnrolledCount > 0 || trainingView.activeEnrollmentsCount > 0) &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.SubscribersText")}
                        </Typography>
                        <ul className={AppGlobalClassKeys.unorderedListFlat}>
                            {trainingView.usersEnrolledCount > 0 &&
                                <Typography variant="subtitle1" component="li" color="black">
                                    {enableViewEnrolledUsers ?
                                        <MprcLink variant="inherit" onClick={() => handleViewEnrollments(EnrolledUsersOption.Enrolled)} color="primary.light">
                                            {t("Training.Literals.EnrollmentsLong", { count: trainingView.usersEnrolledCount })}
                                        </MprcLink>
                                        :
                                        <span>{t("Training.Literals.EnrollmentsLong", { count: trainingView.usersEnrolledCount })}</span>
                                    }
                                    <span>&nbsp;</span>
                                    <span>{t("Training.Literals.SubscribersComplementText", { count: trainingView.usersEnrolledCount })}</span>
                                </Typography>
                            }
                            {trainingView.activeEnrollmentsCount > 0 &&
                                <Typography variant="subtitle1" component="li" color="black">
                                    {enableViewEnrolledUsers ?
                                        <MprcLink variant="inherit" onClick={() => handleViewEnrollments(EnrolledUsersOption.Active)} color="primary.light">
                                            {t("Training.Literals.EnrollmentsLong", { count: trainingView.activeEnrollmentsCount })}
                                        </MprcLink>
                                        :
                                        <span>{t("Training.Literals.EnrollmentsLong", { count: trainingView.activeEnrollmentsCount })}</span>
                                    }
                                    <span>&nbsp;</span>
                                    {
                                        training.multipleClasses
                                            ? t("Training.Literals.ActiveSubscribersComplementMultiText", { count: trainingView.activeEnrollmentsCount })
                                            : t("Training.Literals.ActiveSubscribersComplementSingleText", { count: trainingView.activeEnrollmentsCount })
                                    }
                                </Typography>
                            }
                        </ul>
                    </MprcContainer>
                }

            </>
        }

        {training?.fullManaged == true &&
            <>
                <MprcContainer><Divider /></MprcContainer>
                <EnrollmentViewCalendar
                    enrollmentId={!isPreview ? enrollmentId : null}
                    classroomId={!isPreview ? null : classroomId}
                    isPreview={isPreview}
                />
            </>
        }
    </>;
};
